import { Button } from '@smartgift/frontend-toolkit';
import debounce from 'lodash/debounce';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';

import { PageLayout } from '@hero/components/layouts';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { configs } from '@hero/configs';
import {
  APPROVAL_STATUSES,
  CARDISLE_GREETING_CARD,
  CONTACT_TYPE,
  PAYMENT_METHOD_TYPES,
  SEND_GIFT_FLOW_STEPS,
  RECOGNITION_STATUSES,
  SHARING_METHODS,
  SEND_GIFT_REDIRECT_STATUSES,
  RECOGNITION_CONTACTS_TAB,
  PRODUCT_TYPES,
  GIFT_MESSAGE_TYPE,
  VALIDATION_ERRORS,
  FLOW_CODES,
  RECOGNITION_CREATION_STEPS
} from '@hero/constants';
import { useMaxContactHasError, usePreviousValue } from '@hero/hooks';
import { useSendGiftReviewHeader } from '@hero/hooks/send-gift/useSendGiftReviewHeader';
import {
  ContactSection,
  GiftCollectionSection,
  GiftSetupSection,
  HowToSendSection,
  PricingSection,
  RecipientExperienceSection,
  ReviewFooter
} from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useGetRecognition,
  useGetRecognitionProducts,
  useGetRecognitionRecipientList,
  useGetRecognitionSpendingSummary,
  useRecognitionSharePreview,
  useUpdateRecognition
} from '@hero/services';
import { useGetCSEntries } from '@hero/services/content-stack-service/hooks';
import { useGetBranding } from '@hero/services/organization-service/hooks';
import { useValidateRules } from '@hero/store/hooks';
import { cleanObjectEmptyFields, getAddonData, hasAnyAvailableProducts } from '@hero/utils';
import { getReviewAndPaySchema } from '@hero/validations';

import * as S from './style';

export function SendGiftReviewTemplate() {
  const navigate = useNavigate();
  const { recognitionCode } = useParams();
  const { t } = useTranslation();

  const { data: recognition, isLoading: isLoadingRecognition } = useGetRecognition(
    {
      recognitionCode
    },
    {
      onSuccess: data => {
        if (SEND_GIFT_REDIRECT_STATUSES.includes(data?.status?.current)) {
          navigate(ROUTE_PATHS.MY_RECOGNITION_DETAIL(data?.code));
        }
      }
    }
  );

  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();

  const header = useSendGiftReviewHeader({
    recognition,
    onUpdate: body => {
      updateRecognition({
        params: { recognitionCode },
        body
      });
    }
  });

  const {
    data: recognitionSpendingSummary,
    isLoading: isLoadingSpendingSummary,
    isError: isErrorSpendingSummary
  } = useGetRecognitionSpendingSummary({
    recognitionCode
  });

  const { mutateAsync: recognitionSharePreview } = useRecognitionSharePreview({
    recognitionCode
  });

  const { errors, payAndSend } = useRecognitionPayAndSend({ recognition });

  const noRecipient = errors?.warning?.find(w => w.key.includes('noRecipient'));
  const maxRecipientError = errors?.warning?.find(w => w.key.includes('maxRecipientError'));
  const howToSendSectionErrors = errors?.warning?.find(w =>
    w.key.includes('getSharingMethodsRule')
  );
  const contactSectionErrors = [noRecipient, maxRecipientError].filter(Boolean);
  const hasSenderNameError = errors?.warning?.find(w => w.key.includes('getSenderNameRule'));
  const hasMessageError = errors?.warning?.find(w => w.key.includes('getMessageRule'));

  async function handleUpdateBranding({ logoUrl, giftWrapperUrl }) {
    const body = cleanObjectEmptyFields({
      experienceSettings: {
        branding: {
          ...recognition?.experienceSettings?.branding
        }
      }
    });

    await updateRecognition({
      params: { recognitionCode },
      body: {
        experienceSettings: {
          branding: {
            ...body.experienceSettings.branding,
            ...(logoUrl && { logoUrl: logoUrl === 'null' ? '' : logoUrl }),
            ...(giftWrapperUrl && { giftWrapperUrl })
          }
        }
      }
    });
  }

  function handleUpdateMessageParams({ message, senderName }) {
    const body = cleanObjectEmptyFields({
      experienceSettings: {
        messageParams: { message, senderName }
      }
    });
    updateRecognition({
      params: { recognitionCode },
      body
    });
  }

  function handleUpdateAddons(addons) {
    const filteredAddons = recognition.addons.filter(
      addon => addon?.type !== CARDISLE_GREETING_CARD
    );
    updateRecognition({
      params: { recognitionCode },
      body: {
        addons: [...(filteredAddons.length > 0 ? filteredAddons : []), addons]
      }
    });
  }

  let sharingMethod;
  if (recognition?.experienceSettings?.sharingMethod?.type?.includes(SHARING_METHODS.email)) {
    sharingMethod = SHARING_METHODS.email;
  } else if (recognition?.experienceSettings?.sharingMethod?.type?.includes(SHARING_METHODS.none)) {
    sharingMethod = SHARING_METHODS.none;
  }

  const { data: dataGiftTemplates } = useGetCSEntries(
    {
      entryCode: 'sgb_dashboard_gift_templates',
      params: { query: { uid: { $eq: recognition?.meta?.occasionId } } }
    },
    { enabled: !!recognition?.meta?.occasionId }
  );

  const charities = useMemo(() => {
    return recognition?.products?.filter(p => p?.meta?.productType === PRODUCT_TYPES.CHARITY);
  }, [recognition?.products]);

  const isRecipientStepCompleted = !!recognition?.contacts?.maxCount;
  const isHowToSendStepCompleted = !!recognition?.experienceSettings?.sharingMethod?.type;
  const isHowRecipientsWillSeeStepCompleted =
    !!recognition?.experienceSettings?.messageParams?.message &&
    !!recognition?.experienceSettings?.messageParams?.senderName;

  const isStandardFlowRedirectAllowed = useMemo(() => {
    return (
      isRecipientStepCompleted || isHowToSendStepCompleted || isHowRecipientsWillSeeStepCompleted
    );
  }, [recognition]);

  const howToSendSectionWarnings =
    contactSectionErrors?.length && !!recognition?.experienceSettings?.sharingMethod?.type?.length
      ? [
          <Trans
            key="getContactsRule_order=20"
            i18nKey={VALIDATION_ERRORS.noRecipientAddedAbove}
            values={{ featureName: 'recognition' }}
          />
        ]
      : howToSendSectionErrors
        ? [howToSendSectionErrors]
        : [];

  return (
    <PageLayout
      header={header}
      isHeaderLoading={!!isLoadingUpdateRecognition || !!isLoadingRecognition}
    >
      {isStandardFlowRedirectAllowed && (
        <Button
          variant="link"
          onClick={async () => {
            await updateRecognition({
              params: { recognitionCode },
              body: {
                flowCode: FLOW_CODES.standard,
                meta: {
                  creationStep: RECOGNITION_CREATION_STEPS.newRecognitionContactType
                }
              }
            });
            navigate(ROUTE_PATHS.NEW_RECOGNITION_CONTACT_TYPE);
          }}
          style={{ marginLeft: 'auto', color: 'var(--color-fg-weak)' }}
        >
          {t('continueWithTheStandardFlow')}
        </Button>
      )}

      <S.SendGiftReviewTemplate>
        <GiftCollectionSection
          recognitionCode={recognitionCode}
          products={recognition?.products}
          onEdit={() => {
            const templateOccasionId = recognition?.meta?.occasionId;
            const template = dataGiftTemplates?.entries?.[0];
            if (templateOccasionId && template) {
              navigate({
                pathname: ROUTE_PATHS.SEND_GIFT_UPDATE_GIFT_LIST(recognitionCode),
                search: `?${createSearchParams(
                  cleanObjectEmptyFields({
                    collectionIds: template?.product_criteria?.collection_id,
                    categoryIds: template?.product_criteria?.category_id,
                    brandIds: template?.product_criteria?.brand_id,
                    minPrice: template?.product_criteria?.min_price,
                    maxPrice: template?.product_criteria?.max_price
                  })
                )}`
              });
            } else {
              navigate(ROUTE_PATHS.SEND_GIFT_UPDATE_GIFT_LIST(recognitionCode));
            }
          }}
          stepIndex={1}
          isCompleted={!!recognition?.products?.length}
        />

        <ContactSection
          stepIndex={2}
          warnings={
            !!errors?.warning?.length && !recognition?.contacts?.maxCount
              ? [
                  <Trans
                    key="getContactsRule_order=20"
                    i18nKey={VALIDATION_ERRORS.noRecipientAdded}
                    values={{ featureName: 'recognition' }}
                  />
                ]
              : contactSectionErrors?.length
                ? contactSectionErrors
                : []
          }
          updateContact={async ({ contacts, settings }) => {
            await updateRecognition({
              params: { recognitionCode },
              body: {
                contacts,
                settings
              }
            });
          }}
          recognition={recognition}
          isCompleted={!!recognition?.contacts?.maxCount}
          defaultActiveTab={recognition?.contacts?.type}
        />

        <HowToSendSection
          warnings={howToSendSectionWarnings}
          secureSingleLink={recognition?.link?.preview}
          stepIndex={3}
          sharingMethod={sharingMethod}
          isCompleted={!!sharingMethod}
          isContactless={recognition?.contacts?.type === RECOGNITION_CONTACTS_TAB.number}
          isLoading={!!isLoadingUpdateRecognition || !!isLoadingRecognition}
          onSharingMethod={async ({ sharingMethod, subject }) => {
            await updateRecognition({
              params: { recognitionCode },
              body: {
                experienceSettings: {
                  ...recognition?.experienceSettings,
                  sharingMethod: cleanObjectEmptyFields({
                    type: sharingMethod,
                    ...(sharingMethod === SHARING_METHODS.email && { params: { subject } })
                  })
                }
              }
            });
          }}
          onSharePreview={async ({ recipients }) => {
            const params = { recipients };
            await recognitionSharePreview(params);
          }}
        />

        <RecipientExperienceSection
          warnings={[hasSenderNameError, hasMessageError].filter(Boolean)}
          giftWrapperUrl={recognition?.experienceSettings?.branding?.giftWrapperUrl}
          linkPreview={recognition?.link?.preview}
          logoUrl={recognition?.experienceSettings?.branding?.logoUrl}
          message={recognition?.experienceSettings?.messageParams?.message}
          senderName={recognition?.experienceSettings?.messageParams?.senderName}
          digitalGreetingCard={getAddonData(recognition?.addons, CARDISLE_GREETING_CARD)}
          onUpdateAddons={handleUpdateAddons}
          onMessageParams={handleUpdateMessageParams}
          onBranding={handleUpdateBranding}
          defaultActiveTab={
            recognition?.experienceSettings?.messageParams?.message
              ? GIFT_MESSAGE_TYPE.inputMessage
              : getAddonData(recognition?.addons, CARDISLE_GREETING_CARD)
                ? GIFT_MESSAGE_TYPE.cardMessage
                : null
          }
          stepIndex={4}
          experienceSettings={recognition?.experienceSettings}
          isContactLess={recognition?.contacts?.type === CONTACT_TYPE.number}
          isCompleted={
            !!recognition?.experienceSettings?.messageParams?.message &&
            !!recognition?.experienceSettings?.messageParams?.senderName
          }
        />

        <GiftSetupSection
          expirationDate={recognition?.settings?.expiresAt}
          onDateSelect={debounce(async expirationDate => {
            await updateRecognition({
              params: { recognitionCode },
              body: { settings: { expiresAt: expirationDate } }
            });
          }, 500)}
          stepIndex={5}
          isCompleted={!!recognition?.settings?.expiresAt}
        />

        <PricingSection
          adjustedEstimatedCharge={recognitionSpendingSummary?.adjustedEstimatedCharge}
          refund={recognitionSpendingSummary?.refund}
          estimatedCharges={recognitionSpendingSummary?.estimatedCharges}
          featureType="recognition"
          isCCPayment={recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard}
          isError={isErrorSpendingSummary}
          isLoadingFeature={isLoadingRecognition}
          isLoadingSpendingSummary={isLoadingSpendingSummary}
          stepIndex={6}
          isCompleted={
            recognitionSpendingSummary?.adjustedEstimatedCharge?.total ||
            recognitionSpendingSummary?.estimatedCharges?.total
          }
        />
        <ReviewFooter
          isLoading={isLoadingUpdateRecognition}
          settings={recognition?.settings}
          charities={charities}
          onSettings={async (settings, charities) => {
            const body = {
              settings
            };

            const allProductsByTypeProduct = recognition?.products?.filter(
              p => p?.meta?.productType === PRODUCT_TYPES.PRODUCT
            );
            const allProducts = [...allProductsByTypeProduct, ...charities];
            body.products = allProducts.filter(
              (product, index, self) =>
                index === self.findIndex(t => t.merchantProductId === product.merchantProductId)
            );

            await updateRecognition({
              params: { recognitionCode },
              body
            });
          }}
          onSubmit={async () => {
            const isValid = await payAndSend();
            if (!isValid) return;

            await updateRecognition({
              params: { recognitionCode },
              body: {
                meta: {
                  creationStep: SEND_GIFT_FLOW_STEPS.checkout
                }
              }
            });

            navigate(ROUTE_PATHS.SEND_GIFT_CHECKOUT(recognitionCode));
          }}
        />
      </S.SendGiftReviewTemplate>
    </PageLayout>
  );
}

function useRecognitionPayAndSend({ recognition }) {
  const { t } = useTranslation();

  const { data: brandingData } = useGetBranding();

  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    { code: recognition?.code },
    { enabled: !!recognition?.code }
  );
  const { data: currentProducts } = useGetRecognitionProducts(recognition?.code, {
    enabled: !!recognition?.code
  });

  const contacts = usePreviousValue(recognition?.contacts);
  const maxContactHasError = useMaxContactHasError({
    recognition,
    recognitionCode: recognition?.code,
    contacts: recognition?.contacts
  });

  const { addError, errors } = useValidationErrorsStore();
  const { validate } = useValidateRules();

  const checkPayAndSendCustomErrors = async () => {
    let hasCustomError = false;
    if (contacts?.type === CONTACT_TYPE.list && !recognitionRecipients?.totalDocs) {
      await addError(
        <Trans
          key="getContactsTypeRule_order=20"
          i18nKey="errors.noRecipient"
          components={{ strong: <a href="#section-recipient-experience" /> }}
        />
      );
      hasCustomError = true;
    }
    if (maxContactHasError) {
      await addError(
        <Trans
          key="maxRecipientError_order=21"
          i18nKey="errors.maxRecipientError"
          components={{ strong: <a href="#section-recipient-experience" /> }}
        />
      );
      hasCustomError = true;
    }
    if (!!currentProducts && !hasAnyAvailableProducts(currentProducts)) {
      await addError(
        <Trans
          key="noAvailableProducts"
          i18nKey="noAvailableProducts"
          values={{ feature: t('oneTimeGift') }}
        />
      );
      hasCustomError = true;
    }

    return hasCustomError;
  };

  const isApprovalRequired = recognition?.approval?.status === APPROVAL_STATUSES.required;
  const isApprovalPending = recognition?.status?.current === RECOGNITION_STATUSES.pending_approval;
  const isApprovalBoxShown =
    isApprovalRequired && recognition?.status?.current === RECOGNITION_STATUSES.draft;
  const isCancelApprovalBoxShown = recognition && isApprovalRequired && isApprovalPending;
  const isApprovalProcessing = isApprovalBoxShown || isCancelApprovalBoxShown;
  const digitalGreetingCard =
    configs?.experience?.greetingCardEnabled &&
    getAddonData(recognition?.addons, CARDISLE_GREETING_CARD);

  const checkPayAndSendIsValid = async () => {
    const schemaFunc = await getReviewAndPaySchema();
    const schemaRule = schemaFunc({
      isPaymentMethodRequired: false, // we should allow the go without payment method in here.
      isEndDateRequired: !isApprovalProcessing,
      isInputMessageRequired: !digitalGreetingCard,
      flowCode: recognition?.flowCode
    });

    const isValid = await validate([
      {
        rule: schemaRule,
        value: {
          ...recognition,
          experienceSettings: {
            ...recognition.experienceSettings,
            branding: {
              ...recognition.experienceSettings.branding,
              giftWrapperUrl:
                recognition.experienceSettings.branding?.giftWrapperUrl ||
                brandingData?.branding?.giftWrapperUrl,
              logoUrl:
                recognition.experienceSettings.branding?.logoUrl || brandingData?.branding?.logoUrl
            },
            messageParams: {
              ...recognition.experienceSettings.messageParams,
              digitalGreetingCard: !!digitalGreetingCard
            }
          }
        }
      }
    ]);

    const hasCustomError = await checkPayAndSendCustomErrors();
    return isValid && !hasCustomError;
  };

  return {
    payAndSend: async () => {
      const isPageValid = await checkPayAndSendIsValid();
      return !!isPageValid || !!isApprovalProcessing;
    },
    errors
  };
}
